import { useState, useCallback, useEffect, useRef } from "react";
import { useAuth } from "@/contexts/AuthContext";
import {
  fetchDeckModeProgress,
  upsertDeckModeProgress,
  fetchDeckGameProgress,
  upsertDeckGameProgress,
  type DeckModeProgress,
  type DeckModeKey,
} from "@/lib/db";

const STORAGE_KEY = "ld_deck_mode_progress";
const STORAGE_KEY_GAMES = "ld_deck_game_progress";

/** All game IDs used for average mastery calculation */
export const GAME_IDS = [
  "match", "cat-mouse", "defender", "tower", "memory-jumble", "memory",
  "rebus", "iq", "audio", "blitz", "swipe", "mosaic",
] as const;

function getLocalProgress(deckId: string): DeckModeProgress {
  try {
    const raw = localStorage.getItem(`${STORAGE_KEY}_${deckId}`);
    if (raw) {
      const parsed = JSON.parse(raw) as Partial<DeckModeProgress>;
      return {
        cards: parsed.cards ?? 0,
        learn: parsed.learn ?? 0,
        test: parsed.test ?? 0,
        games: parsed.games ?? 0,
        philology: parsed.philology ?? 0,
      };
    }
  } catch {
    /* ignore */
  }
  return { cards: 0, learn: 0, test: 0, games: 0, philology: 0 };
}

function setLocalProgress(deckId: string, mode: DeckModeKey, pct: number): void {
  try {
    const current = getLocalProgress(deckId);
    current[mode] = Math.min(100, Math.max(0, Math.round(pct)));
    localStorage.setItem(`${STORAGE_KEY}_${deckId}`, JSON.stringify(current));
  } catch {
    /* ignore */
  }
}

function getLocalGameProgress(deckId: string): Record<string, number> {
  try {
    const raw = localStorage.getItem(`${STORAGE_KEY_GAMES}_${deckId}`);
    if (raw) {
      const parsed = JSON.parse(raw) as Record<string, number>;
      return typeof parsed === "object" ? parsed : {};
    }
  } catch {
    /* ignore */
  }
  return {};
}

function setLocalGameProgress(deckId: string, gameId: string, pct: number): void {
  try {
    const current = getLocalGameProgress(deckId);
    const rounded = Math.min(100, Math.max(0, Math.round(pct)));
    current[gameId] = Math.max(current[gameId] ?? 0, rounded);
    localStorage.setItem(`${STORAGE_KEY_GAMES}_${deckId}`, JSON.stringify(current));
  } catch {
    /* ignore */
  }
}

function computeGamesAverage(gameProgress: Record<string, number>): number {
  if (GAME_IDS.length === 0) return 0;
  const sum = GAME_IDS.reduce((acc, id) => acc + (gameProgress[id] ?? 0), 0);
  return Math.round(sum / GAME_IDS.length);
}

export function useDeckModeProgress(deckId: string | undefined, totalCards: number) {
  const { user } = useAuth();
  const [progress, setProgress] = useState<DeckModeProgress>({
    cards: 0,
    learn: 0,
    test: 0,
    games: 0,
  });
  const [gameProgress, setGameProgress] = useState<Record<string, number>>({});
  const [newlyMasteredGameIds, setNewlyMasteredGameIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const fetchRef = useRef<() => void>(() => {});

  const doFetch = useCallback(() => {
    if (!deckId) {
      setProgress({ cards: 0, learn: 0, test: 0, games: 0, philology: 0 });
      setGameProgress({});
      setLoading(false);
      return;
    }

    // Always load from localStorage first so UI has data immediately (no DB dependency)
    const localGame = getLocalGameProgress(deckId);
    const localMode = getLocalProgress(deckId);

    if (user) {
      setLoading(true);
      setGameProgress(localGame);
      setProgress((prev) => ({ ...prev, ...localMode, games: computeGamesAverage(localGame) }));

      Promise.all([
        fetchDeckModeProgress(user.id, deckId),
        fetchDeckGameProgress(user.id, deckId),
      ])
        .then(([modeData, gameData]) => {
          const dbGameData = Object.keys(gameData).length > 0 ? gameData : null;
          const merged = dbGameData
            ? GAME_IDS.reduce(
                (acc, id) => {
                  acc[id] = Math.max(localGame[id] ?? 0, dbGameData[id] ?? 0);
                  return acc;
                },
                {} as Record<string, number>
              )
            : localGame;
          setGameProgress(merged);
          setProgress((prev) => ({ ...prev, ...modeData, games: computeGamesAverage(merged) }));
          console.log("[GAMES DEBUG] gameProgress:", JSON.stringify(merged));
        })
        .catch(() => {
          setGameProgress(localGame);
          setProgress((prev) => ({ ...prev, ...localMode, games: computeGamesAverage(localGame) }));
          console.log("[GAMES DEBUG] gameProgress (localStorage fallback):", JSON.stringify(localGame));
        })
        .finally(() => setLoading(false));
    } else {
      setProgress({ ...localMode, games: computeGamesAverage(localGame) });
      setGameProgress(localGame);
      setLoading(false);
      console.log("[GAMES DEBUG] gameProgress (guest):", JSON.stringify(localGame));
    }
  }, [deckId, user?.id]);

  useEffect(() => {
    fetchRef.current = doFetch;
    doFetch();
  }, [doFetch]);

  const refetchGameProgress = useCallback(() => {
    fetchRef.current();
  }, []);

  const updateProgress = useCallback(
    (mode: DeckModeKey, progressPct: number) => {
      if (!deckId || totalCards <= 0) return;
      const pct = Math.min(100, Math.max(0, Math.round(progressPct)));
      setProgress((prev) => ({ ...prev, [mode]: pct }));
      if (user) {
        upsertDeckModeProgress(user.id, deckId, mode, pct).catch(() => {});
      } else {
        setLocalProgress(deckId, mode, pct);
      }
    },
    [deckId, user?.id, totalCards]
  );

  const updateGameProgress = useCallback(
    (gameId: string, progressPct: number) => {
      if (!deckId) return;
      const pct = Math.min(100, Math.max(0, Math.round(progressPct)));
      setGameProgress((prev) => {
        const prevPct = prev[gameId] ?? 0;
        const next = { ...prev, [gameId]: Math.max(prevPct, pct) };
        setProgress((p) => ({ ...p, games: computeGamesAverage(next) }));
        if (pct === 100 && prevPct < 100) {
          setNewlyMasteredGameIds((s) => new Set(s).add(gameId));
        }
        return next;
      });
      setLocalGameProgress(deckId, gameId, pct);
      if (user) {
        upsertDeckGameProgress(user.id, deckId, gameId, pct).catch(() => {});
      }
    },
    [deckId, user?.id]
  );

  const clearNewlyMastered = useCallback((gameId: string) => {
    setNewlyMasteredGameIds((s) => {
      const next = new Set(s);
      next.delete(gameId);
      return next;
    });
  }, []);

  const clearAllNewlyMastered = useCallback(() => {
    setNewlyMasteredGameIds(new Set());
  }, []);

  return { progress, gameProgress, loading, updateProgress, updateGameProgress, refetchGameProgress, newlyMasteredGameIds, clearNewlyMastered, clearAllNewlyMastered };
}
